import type { PlayableRoomGameId } from "./roomGameCatalog";

export interface GameGuide {
  id: PlayableRoomGameId;
  name: string;
  symbol: string;
  color: string;
  tagline: string;
  summary: string;
  players: string;
  time: string;
  history: string[];
  setup: string[];
  goal: string;
  lessons: Array<{ title: string; outcome: string }>;
  steps: string[];
  rules: string[];
  tips: string[];
}

export const gameGuides: GameGuide[] = [
  {
    id: "tic-tac-toe",
    name: "Tic Tac Toe",
    symbol: "XO",
    color: "blue",
    tagline: "Three marks. One clean line.",
    summary: "Take turns placing X or O on a 3 × 3 board. It is quick to learn and quick to play.",
    players: "2 players or 1 player with a bot",
    time: "About 1–3 minutes",
    history: [
      "Three-in-a-row games are ancient, but they did not all use today’s rules. Roman players used a related game called terni lapilli, where each side had only three movable pieces.",
      "The familiar paper-and-pencil form became widely known as noughts and crosses in Britain. The American name tic-tac-toe became common in the twentieth century. There is no single known inventor of the modern game.",
      "Its tiny game tree also made it an early showcase for computing: researchers built machines that could play it decades before home video games.",
    ],
    setup: [
      "The board starts as nine empty squares in a 3 × 3 grid.",
      "One player uses X and the other uses O. X takes the first turn.",
      "If a room clock is enabled, each player must move before their remaining time reaches zero.",
    ],
    goal: "Be first to place three of your marks in a row, column, or diagonal.",
    lessons: [
      { title: "Your first mark", outcome: "Choose an empty square and learn turn order." },
      { title: "Rows, columns, diagonals", outcome: "Recognise every kind of winning line." },
      { title: "Block a threat", outcome: "Stop an opponent who has two marks in line." },
      { title: "Create a fork", outcome: "Build two winning threats with one move." },
      { title: "Finish the line", outcome: "Play the winning square before the board fills." },
    ],
    steps: [
      "Choose an empty square when it is your turn.",
      "Your mark stays in that square for the rest of the round.",
      "The round ends when someone makes a line of three or every square is filled.",
    ],
    rules: [
      "You cannot choose an occupied square.",
      "Only one mark may be placed per turn, and placed marks never move.",
      "Rows, columns, and the two corner-to-corner diagonals all count.",
      "The first completed line ends the round immediately; later lines are not played.",
      "A full board with no line of three is a draw.",
      "Timed rounds end when a player's clock runs out.",
    ],
    tips: [
      "The centre gives you the most ways to make a line.",
      "Block an opponent who already has two marks in a line.",
      "Try to create two possible winning lines at once.",
    ],
  },
  {
    id: "chess",
    name: "Chess",
    symbol: "♞",
    color: "sky",
    tagline: "Protect your king. Outsmart theirs.",
    summary: "Move your pieces across the board, build an attack, and keep your own king safe.",
    players: "2 players or 1 player with a bot",
    time: "Varies with the chosen clock",
    history: [
      "Chess developed from chaturanga in north-western India around the sixth century. Its pieces represented an army’s infantry, cavalry, elephants, and chariots.",
      "The game travelled through Persia and the Islamic world into Europe. Its name and the phrase checkmate preserve Persian roots connected to the king.",
      "European rule changes in the late medieval period made the queen and bishop much more powerful, creating the faster modern game. International tournament rules were later standardised across organised chess.",
    ],
    setup: [
      "Place the board with a light square at each player’s right-hand corner.",
      "The back rank is rook, knight, bishop, queen, king, bishop, knight, rook; the queen begins on her own colour. Eight pawns fill the rank in front.",
      "White moves first. Each side starts with one king, one queen, two rooks, two bishops, two knights, and eight pawns.",
    ],
    goal: "Checkmate the other king so it is under attack and has no legal escape.",
    lessons: [
      { title: "Board coordinates", outcome: "Read files a–h and ranks 1–8." },
      { title: "Pawn", outcome: "Advance, capture diagonally, promote, and recognise en passant." },
      { title: "Knight", outcome: "Jump in an L shape over occupied squares." },
      { title: "Bishop", outcome: "Travel diagonally while staying on one colour." },
      { title: "Rook", outcome: "Control ranks and files without jumping." },
      { title: "Queen", outcome: "Combine rook and bishop movement." },
      { title: "King", outcome: "Move safely and respond to check." },
      { title: "Castling", outcome: "Move king and rook together when every condition is legal." },
      { title: "Checkmate", outcome: "Attack the king while covering every escape." },
    ],
    steps: [
      "White moves first, then players take one turn at a time.",
      "Select one of your pieces to see where it may legally move.",
      "Capture by moving onto a square occupied by an opposing piece.",
      "Keep playing until checkmate, a draw, resignation, or time runs out.",
    ],
    rules: [
      "You may never make a move that leaves your king in check.",
      "Kings move one square; queens move any distance straight or diagonally; rooks move straight; bishops move diagonally; knights jump in an L shape.",
      "Pawns move forward one square, may move two from their starting rank, and capture one square diagonally forward.",
      "Castling is legal only when the king and rook have not moved, the path is empty, and the king does not start in, cross, or finish in check.",
      "En passant is available only immediately after an adjacent enemy pawn advances two squares. A pawn reaching the final rank must promote, usually to a queen.",
      "Checkmate wins. Stalemate, insufficient material, threefold repetition, the fifty-move rule, or an agreed draw can end without a winner.",
      "In timed chess, running out of time loses unless the opponent has no possible mating material under the supported rule set.",
    ],
    tips: [
      "Bring out knights and bishops early, then castle your king.",
      "Before every move, check what your opponent is attacking.",
      "Do not trade a valuable piece without a good reason.",
    ],
  },
  {
    id: "ludo",
    name: "Classic Indian Ludo",
    symbol: "⚄",
    color: "coral",
    tagline: "Roll, race, capture, and come home.",
    summary: "Race all four gotis from your yard around the board and into your home path.",
    players: "2–4 players or play with a bot",
    time: "About 15–40 minutes",
    history: [
      "Ludo is a streamlined descendant of Pachisi, the cross-shaped race game played in India for centuries. Pachisi was played with cowrie shells and often on a cloth board.",
      "Large Pachisi courts survive at Mughal sites associated with Emperor Akbar, showing how the game became part of courtly as well as family play.",
      "A simplified single-die version was registered in Britain as Ludo in the 1890s. The name comes from Latin for ‘I play’; regional Indian house rules remain an important part of how families play today.",
    ],
    setup: [
      "Each player receives four gotis in one colour and places them in the matching yard.",
      "The shared outer track runs around the cross. Each colour has a protected final lane leading to the centre home.",
      "Topzle seats two players opposite each other and fills open colour slots in arrival order for three or four players.",
    ],
    goal: "Bring all four of your gotis safely into the centre before everyone else.",
    lessons: [
      {
        title: "Read the full board",
        outcome: "Find yards, starting squares, safe stars, and home lanes.",
      },
      { title: "Release a goti", outcome: "Roll a six and enter on your coloured start." },
      { title: "Count a move", outcome: "Move one square at a time by the dice value." },
      { title: "Capture safely", outcome: "Send an opponent home only on an unsafe square." },
      { title: "Enter the home lane", outcome: "Unlock the lane and finish with an exact roll." },
    ],
    steps: [
      "Roll a six to bring a goti out of your yard.",
      "Choose a glowing goti and move it by the rolled number.",
      "Land on an opponent on an unsafe square to send that goti back to its yard.",
      "Use an exact roll to bring each goti into the centre.",
    ],
    rules: [
      "Each turn gives you 30 seconds to throw, then 30 seconds to choose a goti. If time expires, Topzle rolls or chooses a legal goti automatically.",
      "When only one goti can use a roll, it pulses and moves automatically after the dice lands.",
      "Everyone in the room sees the same dice throw and result.",
      "Star squares and each colour's final home path are safe from captures.",
      "A capture unlocks entry into your final home path in this Topzle room rule set.",
      "A playable six gives another roll. Three sixes in a row cancel the turn.",
      "A six with no legal move does not give an extra turn.",
      "A goti may enter the track from the yard only on a six. If more than one move is legal, the player chooses; a sole legal move is automatic.",
      "Landing on an unsafe square occupied by one opponent goti captures it. Gotis in yards, coloured home lanes, and marked safe squares cannot be captured.",
      "A goti cannot enter the protected final lane until that player has made at least one capture, and it needs the exact remaining count to finish.",
      "With two players, the colours sit diagonally opposite each other.",
    ],
    tips: [
      "Move onto safe squares when an opponent is close behind.",
      "Keep more than one goti active so one capture does not stop your race.",
      "Balance chasing a capture with moving your leading goti home.",
      "The host can switch Fast mode on or off inside the game for shorter dice and move transitions.",
    ],
  },
  {
    id: "snakes-ladders",
    name: "Snakes & Ladders",
    symbol: "🐍",
    color: "green",
    tagline: "Climb fast. Dodge the bite.",
    summary:
      "Race from square 1 to 100 while ladders launch you ahead and snakes send you sliding back.",
    players: "2–4 players or play with a bot",
    time: "About 8–20 minutes",
    history: [
      "Snakes & Ladders descends from Indian games known by names including Moksha Patam and Gyan Chaupar. Historic boards used the journey as a lesson about conduct, karma, and spiritual progress.",
      "Ladders represented virtues that lifted a player, while snakes represented vices and their consequences. The exact board, language, and number of squares varied by region and period.",
      "The game reached Britain in the nineteenth century, where much of its moral text was simplified into a children’s race. It later became Chutes and Ladders in the United States.",
    ],
    setup: [
      "All gotis begin before square 1, and the board snakes through numbered rows from 1 to 100.",
      "Ladders connect a lower square to a higher one. A snake’s head is on the higher square and its tail on the lower square.",
      "Topzle supports two to four seats and uses one six-sided die.",
    ],
    goal: "Be the first player to land exactly on square 100.",
    lessons: [
      { title: "Follow the board", outcome: "Read alternating numbered rows correctly." },
      { title: "Count every step", outcome: "Move by the exact die value." },
      { title: "Climb a ladder", outcome: "Land on its foot and travel to its top." },
      { title: "Meet a snake", outcome: "Land on its head and slide to its tail." },
      { title: "Exact finish", outcome: "Reach 100 without overshooting." },
    ],
    steps: [
      "Roll the dice when it is your turn.",
      "Your goti moves forward one square at a time by the rolled number.",
      "Land at the foot of a ladder to climb, or on a snake's head to be bitten and slide down.",
      "Reach square 100 with an exact roll to win.",
    ],
    rules: [
      "Turns follow the live player order shown in the room.",
      "A roll that would pass square 100 leaves your goti where it is.",
      "Landing exactly on a ladder’s foot moves the goti immediately to its top.",
      "Landing exactly on a snake’s head triggers the bite and moves the goti to its tail.",
      "Only the final landing square triggers a snake or ladder; passing over one does not.",
      "A player has 20 seconds to roll. When the timer expires, Topzle rolls and completes the move automatically.",
      "Players who join while a race is active enter at square 0 when a slot is available.",
      "Up to four players can race; earlier connected arrivals receive open slots first.",
    ],
    tips: [
      "Every roll matters, but the board decides when a shortcut or snake appears.",
      "Keep an eye on the next few snake heads near the finish.",
      "A player far behind can still make a dramatic comeback with a long ladder.",
    ],
  },
  {
    id: "connect-four",
    name: "Connect 4",
    symbol: "●●",
    color: "yellow",
    tagline: "Drop a disc. Build a line.",
    summary: "Take turns dropping coloured discs into a standing grid.",
    players: "2 players or 1 player with a bot",
    time: "About 3–8 minutes",
    history: [
      "Vertical alignment games grew from older pencil-and-paper and board-game ideas, but the familiar plastic Connect Four was created by Howard Wexler and first sold by Milton Bradley in 1974.",
      "Its gravity-driven grid made the rules immediately visible: every piece falls, every threat stays on the board, and a win can appear in four directions.",
      "The game has since become a classic example in mathematics and computer science because perfect play can be analysed despite the many possible positions.",
    ],
    setup: [
      "The board is an empty upright grid with seven columns and six rows.",
      "Each player receives one colour of discs. The starting player is shown in the room.",
      "Discs are not placed in arbitrary cells: every choice is a column, and gravity determines the row.",
    ],
    goal: "Connect four of your discs horizontally, vertically, or diagonally.",
    lessons: [
      { title: "Gravity", outcome: "Choose a column and watch the disc fall." },
      { title: "Horizontal four", outcome: "Complete four neighbours across a row." },
      { title: "Vertical four", outcome: "Stack four discs in one column." },
      { title: "Diagonal four", outcome: "Build a supported rising or falling diagonal." },
      { title: "Block and fork", outcome: "Stop a threat and create two of your own." },
    ],
    steps: [
      "Choose a column when it is your turn.",
      "Your disc falls into the lowest open space in that column.",
      "Players alternate until someone connects four or the board fills.",
    ],
    rules: [
      "You cannot place a disc in a full column.",
      "A line can run left to right, up and down, or diagonally.",
      "A disc stays where it lands and cannot be removed or moved during the round.",
      "The first line of four ends the round immediately, even if the final disc also creates another line.",
      "A full board with no line of four is a draw.",
    ],
    tips: [
      "The middle columns take part in more possible winning lines.",
      "Stop any open line where your opponent already has three.",
      "Create two threats so your opponent cannot block both.",
    ],
  },
  {
    id: "battleship",
    name: "Battleship",
    symbol: "⚓",
    color: "navy",
    tagline: "Place your fleet. Read the sea.",
    summary: "Hide a fleet on your ocean grid, then take turns attacking the enemy waters.",
    players: "2 players or 1 player with a bot",
    time: "About 10–25 minutes",
    history: [
      "Battleship grew from early twentieth-century pencil-and-paper naval games often called Salvo or Battleships. Players secretly mapped fleets and called coordinates to discover an opponent’s ships.",
      "Commercial peg-and-grid versions turned those private paper maps into two hinged boards. Milton Bradley’s 1967 edition helped establish the plastic format many players recognise.",
      "Topzle keeps the central idea—hidden information and deduction—while making placement, hit tracking, and ship abilities visible to each authorised player.",
    ],
    setup: [
      "Each player has a private fleet grid and a separate targeting view of enemy waters.",
      "Select a ship, rotate it if needed, then place all of its blocks in a straight horizontal or vertical line without overlap.",
      "Locking the fleet makes placement final for that round. Combat begins only after both active players are ready.",
    ],
    goal: "Find and sink every enemy ship before your own fleet is destroyed.",
    lessons: [
      { title: "Read coordinates", outcome: "Name a target using its letter and number." },
      { title: "Place a ship", outcome: "Select, rotate, and fit a hull without overlap." },
      { title: "Lock the fleet", outcome: "Confirm placement before combat begins." },
      {
        title: "Fire and read clues",
        outcome: "Tell a miss from a hit and search adjacent water.",
      },
      { title: "Sink the ship", outcome: "Hit every block in the same hull." },
    ],
    steps: [
      "Choose each ship, rotate it if needed, and place it on your fleet board.",
      "Select Lock & Ready when all ships are placed.",
      "On your turn, choose an untried cell on the enemy board.",
      "Use hit and miss clues to find the rest of each ship.",
    ],
    rules: [
      "Ships must stay inside the board and cannot overlap.",
      "Fleet positions are private; opponents see only the result of coordinates already attacked.",
      "A coordinate can be attacked only once. A miss ends that attack without damaging a ship.",
      "A hit damages one section. Every section must be hit to sink that ship.",
      "Sunken ships cannot launch attacks.",
      "Any special attack shown for a ship follows its displayed target shape and availability; invalid or previously attacked cells are rejected.",
      "The game ends when one player's complete fleet is sunk.",
    ],
    tips: [
      "Spread ships out and avoid an easy-to-read pattern.",
      "After a hit, search nearby cells in a straight line.",
      "A checkerboard search pattern can find long ships efficiently.",
    ],
  },
  {
    id: "draw-guess",
    name: "Draw & Guess",
    symbol: "✎",
    color: "pink",
    tagline: "Sketch the clue. Beat the clock.",
    summary: "Draw a secret word while another player—or the on-device bot—tries to name it.",
    players: "Friends or 1 player with the guessing bot",
    time: "About 1 minute per drawing",
    history: [
      "Drawing-and-guessing play grew from charades and informal parlour games in which a picture replaced spoken clues. It has no single originator as a social activity.",
      "The best-known commercial version, Pictionary, was created by Robert Angel with graphic design by Gary Everson and first published in 1985.",
      "Topzle calls its version Draw & Guess and adds an on-device guessing mode so a player can practise without sending drawings to an external AI service.",
    ],
    setup: [
      "The drawer receives a private word while guessers see only the canvas and round timer.",
      "Choose the brush colour and width before or during the drawing; use clear if you need to restart the picture.",
      "In bot mode, the lightweight classifier runs locally in the browser and proposes guesses from the supported word set.",
    ],
    goal: "Help the guesser find the secret word before time runs out.",
    lessons: [
      { title: "Draw a silhouette", outcome: "Give the local recognizer one bold overall shape." },
      {
        title: "Add a useful detail",
        outcome: "Separate the answer from similar objects without letters.",
      },
      { title: "Read a drawing", outcome: "Identify the most distinctive visual clue." },
      { title: "Submit a guess", outcome: "Type and send an answer before the timer ends." },
    ],
    steps: [
      "Read your secret word without saying or typing it.",
      "Draw a simple picture on the canvas.",
      "The guesser submits ideas while the timer runs.",
      "A correct guess ends the round and adds to the room result.",
    ],
    rules: [
      "Do not write the secret word, spell it, or use letters as clues.",
      "Do not say the word or reveal its spelling through chat, numbers, blanks, or sound-alike hints.",
      "Use the brush and eraser to keep the drawing clear.",
      "Only guesses submitted before the timer ends count. Matching is case-insensitive and may accept supported normalised forms.",
      "The drawer may not submit the answer as a guess for their own round.",
      "The local guessing bot works best with a bold, simple black line drawing.",
    ],
    tips: [
      "Start with the biggest, most recognisable shape.",
      "Add one or two details that separate the object from similar things.",
      "Keep lines clear instead of shading the whole canvas.",
    ],
  },
  {
    id: "mafia",
    name: "Mafia",
    symbol: "🎭",
    color: "navy",
    tagline: "Trust carefully. Read everyone.",
    summary:
      "A hidden minority strikes at night while the Town investigates, protects, debates, and votes by day.",
    players: "4–12 players",
    time: "About 10–30 minutes",
    history: [
      "Mafia was created in 1986 by psychology student Dmitry Davidoff at Moscow State University. It grew from classrooms and summer camps into an international party game.",
      "The game became known as Werewolf in a popular themed variation, but both versions share the same core structure: an informed hidden minority against an uninformed majority.",
      "Online versions can act as the moderator, privately deal roles, collect night actions, and count votes, allowing every person in the room to participate as a player.",
    ],
    setup: [
      "Topzle secretly deals every player a role. Never reveal the role card shown on your screen unless bluffing is part of your plan.",
      "The Town team contains Citizens, one Doctor, and one Detective. The Mafia know their teammates; Town players do not.",
      "Keep voice or text conversation focused on the current day. Night choices and Detective results remain private.",
    ],
    goal: "Town wins by eliminating every Mafia member. Mafia wins when living Mafia equal or outnumber the remaining Town.",
    lessons: [
      { title: "Read your dossier", outcome: "Understand your secret role, team, and power." },
      { title: "Act at night", outcome: "Choose a target privately when your role has an action." },
      { title: "Build a case", outcome: "Compare claims, behaviour, and earlier voting patterns." },
      { title: "Cast a ballot", outcome: "Vote for one suspect or abstain when evidence is weak." },
      {
        title: "Track win pressure",
        outcome: "Recognise when the living-player balance is dangerous.",
      },
    ],
    steps: [
      "Read and acknowledge your private role card.",
      "At night, Mafia choose a victim, the Doctor protects someone, and the Detective investigates someone.",
      "At dawn, Topzle reports whether anyone was eliminated or saved without revealing hidden choices.",
      "Discuss suspicions, mark yourself ready, then vote. The cycle repeats until one faction wins.",
    ],
    rules: [
      "Living Mafia may target only non-Mafia players at night. When several Mafia are alive, their target votes must produce a unique leader; tied choices or missing choices at the deadline cause no Mafia elimination.",
      "The Doctor may protect any living player, including themself. A matching protection prevents the Mafia elimination without identifying either target.",
      "The Detective investigates one other living player and privately learns whether that player is Mafia.",
      "Citizens have no night power, but every living player may discuss and vote during the day.",
      "Each living player gets one public ballot. A suspect must lead every other suspect and the abstentions to be eliminated. A tie, an abstention lead, or mostly missing ballots at the deadline eliminates nobody.",
      "Eliminated players become spectators and may not vote, use night powers, or influence the active case through room chat.",
      "All Mafia being eliminated gives Town the win. Mafia equalling or outnumbering the living Town gives Mafia control and ends the game.",
      "Phase deadlines keep a disconnected or undecided player from freezing the room. Missing votes count as abstentions, while missing night powers resolve as no action when time expires.",
    ],
    tips: [
      "Ask for reasoning, not just names. A changing explanation is often more useful than a confident accusation.",
      "As Detective, revealing one result may help Town but also tells Mafia whom to target next.",
      "As Doctor, vary your protections and think about which trusted player Mafia most wants removed.",
      "Record who defended whom and when votes changed; relationships across days expose teams.",
    ],
  },
  {
    id: "rock-paper-scissors",
    name: "Live Rock Paper Scissors",
    symbol: "✊",
    color: "red",
    tagline: "Camera ready. Throw on three.",
    summary:
      "Show rock, paper, or scissors to your camera and let Topzle read the final hand pose.",
    players: "2 players or 1 player with a bot",
    time: "A few seconds per round",
    history: [
      "Three-way hand games were recorded in China and later developed into many Japanese sansukumi-ken games, where each sign defeats one and loses to another.",
      "The rock, paper, and scissors signs took their modern form in Japan and spread internationally in the early twentieth century. No single inventor is known.",
      "The circular balance makes it useful for quick decisions, but repeated rounds also invite observation, bluffing, and pattern-breaking.",
    ],
    setup: [
      "For live mode, open the camera only when you are ready and position one hand inside the guide. Camera access is never required merely to join a room.",
      "Both players choose during the same countdown, so neither choice is revealed early.",
      "Bot mode needs no camera: tap a hand sign and the bot reveals after your choice is locked.",
    ],
    goal: "Win rounds: rock beats scissors, scissors beats paper, and paper beats rock.",
    lessons: [
      { title: "Learn the cycle", outcome: "Know which sign each choice beats and loses to." },
      { title: "Choose on three", outcome: "Lock a clear hand pose at reveal time." },
      { title: "Read the result", outcome: "See the winner colour and the exact winning rule." },
      { title: "Handle a draw", outcome: "Recognise matching signs and play again." },
    ],
    steps: [
      "Allow camera access and keep your hand inside the guide.",
      "Get ready during the countdown.",
      "Hold one clear pose when the countdown reaches zero.",
      "Topzle shows what it detected, then reveals both choices.",
    ],
    rules: [
      "Rock is a closed fist, paper is an open palm, and scissors uses two raised fingers.",
      "Rock beats scissors, scissors beats paper, and paper beats rock. No sign is strongest overall.",
      "Matching poses make a draw.",
      "A choice is locked at the reveal point; changing the hand afterward does not change the round.",
      "If Topzle cannot confidently read a live pose, the round asks for a retry instead of inventing a choice.",
      "The camera view is used for the live game and is not recorded by default.",
    ],
    tips: [
      "Use good light and keep your whole hand visible.",
      "Hold the final pose still for a moment.",
      "Move a little farther from the camera if your fingers are cropped.",
    ],
  },
];

export const gameGuideById = new Map(gameGuides.map((guide) => [guide.id, guide]));
